/* FOM · dimensoes da mesma realidade (Fase 1Z).

   A empresa nao e uma arvore so: ORGANIZACAO (quem somos), PROCESSOS (como o
   trabalho acontece) e PRODUTOS/FLUXOS (o que atravessa a organizacao) sao
   PERSPECTIVAS de navegacao sobre o MESMO dado — nunca bancos, nunca mapas
   paralelos. Cada dimensao aqui aponta para um ramo REAL da BASE congelada:

     organizacao  gni    "Gerente de Negocios Internacionais" (estrutura)
     processos    procs  "Processos ponta a ponta" (fluxo de trabalho)
     produtos     op-*   IM/EM/IA/EA — nos reais tipo `func` sob Operacional

   Os quatro produtos VIVEM como filhos de Operacional/Delivery na BASE (fato
   auditado em FOM-ARQ-0035); a correcao autorizada e de APRESENTACAO: eles
   ganham selo de produto/fluxo e uma leitura de cadeia ponta a ponta — a
   BASE nao se move. */
import type { FomNode, Relation } from '../core/types.ts';

export type Dimensao = 'organizacao' | 'processos' | 'produtos';

export const DIMENSOES: { id: Dimensao; rotulo: string; dica: string; raiz: string | null }[] = [
  { id: 'organizacao', rotulo: 'Organizacao', dica: 'quem somos — estrutura e funcoes', raiz: 'gni' },
  { id: 'processos', rotulo: 'Processos', dica: 'como o trabalho acontece — ponta a ponta', raiz: 'procs' },
  { id: 'produtos', rotulo: 'Produtos', dica: 'o que atravessa a organizacao — IM · EM · IA · EA', raiz: null },
];

/* Os produtos/fluxos reais da BASE — verificados contra a seed em teste. */
export const PRODUTOS = ['op-im', 'op-em', 'op-ia', 'op-ea'] as const;

export function ehProduto(externalId: string): boolean {
  return (PRODUTOS as readonly string[]).includes(externalId);
}

/* PERSPECTIVA ORGANIZACAO (1AE.1): a leitura organizacional exclui do
   LAYOUT as subarvores de tipo `process` penduradas em pais organizacionais
   (org/mgmt/unit/gest). E filtro de APRESENTACAO — mesmo mecanismo da lente
   FUTURA: o dado continua integro, carregado, buscavel e acessivel pela
   perspectiva PROCESSOS. Regra pelo TIPO real, nunca por nome. */
const TIPOS_ORGANIZACIONAIS = ['org', 'mgmt', 'unit', 'gest'];

export function filtrarPerspectivaOrganizacao<T extends {
  external_id: string; parent_external_id: string | null; node_type: string | null;
}>(nos: T[]): { visiveis: T[]; excluidos: number } {
  const porId = new Map(nos.map((n) => [n.external_id, n]));
  const raizesExcluidas = new Set<string>();
  for (const n of nos) {
    if ((n.node_type ?? '') !== 'process') continue;
    const pai = n.parent_external_id ? porId.get(n.parent_external_id) : null;
    if (!pai || TIPOS_ORGANIZACIONAIS.includes(pai.node_type ?? '')) {
      raizesExcluidas.add(n.external_id);
    }
  }
  const excluidosSet = new Set<string>();
  const dentroDeExcluida = (n: T): boolean => {
    let atual: T | undefined = n;
    let guarda = 0;
    while (atual && guarda++ < 64) {
      if (raizesExcluidas.has(atual.external_id)) return true;
      atual = atual.parent_external_id ? porId.get(atual.parent_external_id) : undefined;
    }
    return false;
  };
  const visiveis = nos.filter((n) => {
    if (dentroDeExcluida(n)) { excluidosSet.add(n.external_id); return false; }
    return true;
  });
  return { visiveis, excluidos: excluidosSet.size };
}

/* GATE Financeiro-geometria: na perspectiva ORGANIZACAO, quem se relaciona
   por SUPORTE a uma unit e PROJETADO como irmao espacial dela — o layout
   recebe uma COPIA com parent projetado para o pai da unit. O dado fisico
   (parent_external_id no banco/store) NUNCA e tocado; a relacao de suporte
   continua no conjunto de relacoes e e desenhada pela gramatica de RELACAO
   (tracejada), jamais pela aresta estrutural. Regra por tipo+relacao reais,
   nunca por nome. */
export function projetarPerspectivaOrganizacao<T extends {
  external_id: string; parent_external_id: string | null; node_type: string | null; ordinal: number;
}>(nos: T[], relacoes: { from: string; to: string; type: string }[]): {
  visiveis: T[]; excluidos: number; promovidos: number;
} {
  const { visiveis, excluidos } = filtrarPerspectivaOrganizacao(nos);
  const porId = new Map(visiveis.map((n) => [n.external_id, n]));
  let promovidos = 0;
  const projetados = visiveis.map((n) => {
    for (const r of relacoes) {
      if (r.from !== n.external_id || !/suporte/.test(r.type)) continue;
      const alvo = porId.get(r.to);
      if (!alvo || (alvo.node_type ?? '') !== 'unit' || !alvo.parent_external_id) continue;
      if (n.parent_external_id === alvo.parent_external_id) break;
      promovidos += 1;
      /* copia de PROJECAO: parent e profundidade espaciais iguais aos da
         unit que ele suporta — o objeto original nunca e mutado.
         ORDEM (microgate ORGANIZACAO/Financeiro): o promovido entra
         IMEDIATAMENTE DEPOIS da unit que suporta (ordinal + 0.5 — entre a
         unit e o irmao seguinte), a mesma gramatica da cena organizacional.
         Sem isso, ele herdava o ordinal do vinculo fisico (filho da unit) e
         caia no FIM da fileira, longe de quem suporta. Regra por relacao +
         tipo reais, nunca por id ou nome. */
      const copia = { ...n, parent_external_id: alvo.parent_external_id, ordinal: alvo.ordinal + 0.5 };
      if ('depth' in alvo) (copia as { depth?: unknown }).depth = (alvo as { depth?: unknown }).depth;
      return copia;
    }
    return n;
  });
  return { visiveis: projetados, excluidos, promovidos };
}

export type ElosCadeia = {
  no: FomNode;
  /* o TEXTO da relacao real que liga este elo ao proximo (ou ao contexto);
     null no elo que hospeda o produto */
  relacao: string | null;
  /* papel do elo na leitura: 'etapa' segue o fluxo; 'suporte' e transversal */
  papel: 'etapa' | 'hospeda' | 'suporte';
};

/* A cadeia ponta a ponta de um produto, derivada EXCLUSIVAMENTE de relacoes
   e parentesco reais:
     [X --envia para--> H] [H --recebe--> DEPT (que hospeda o produto)]
     [S --suporte...--> DEPT]
   Se uma aresta nao existir no dado, o elo simplesmente nao aparece — a
   cadeia encolhe com honestidade, nunca se completa com suposicao. */
export function derivarCadeiaDoProduto(
  nos: FomNode[],
  relacoes: Relation[],
  produtoId: string,
): ElosCadeia[] {
  const porId = new Map(nos.map((n) => [n.external_id, n]));
  const produto = porId.get(produtoId);
  if (!produto || !ehProduto(produtoId)) return [];
  const dept = produto.parent_external_id ? porId.get(produto.parent_external_id) : null;
  if (!dept) return [];

  const cadeia: ElosCadeia[] = [];

  /* quem ENTREGA ao departamento que hospeda o produto (ex.: handoff) */
  const entrega = relacoes.find((r) => r.to === dept.external_id && /recebe|envia/.test(r.type));
  if (entrega) {
    const meio = porId.get(entrega.from);
    if (meio) {
      /* e quem entrega ao intermediario (ex.: Comercial -> handoff) */
      const origem = relacoes.find((r) => r.to === meio.external_id && /envia/.test(r.type));
      if (origem) {
        const inicio = porId.get(origem.from);
        if (inicio) cadeia.push({ no: inicio, relacao: origem.type, papel: 'etapa' });
      }
      cadeia.push({ no: meio, relacao: entrega.type, papel: 'etapa' });
    }
  }

  cadeia.push({ no: dept, relacao: null, papel: 'hospeda' });

  /* suporte transversal declarado por relacao real (ex.: Financeiro) */
  for (const r of relacoes.filter((x) => x.to === dept.external_id && /suporte/.test(x.type))) {
    const s = porId.get(r.from);
    if (s) cadeia.push({ no: s, relacao: r.type, papel: 'suporte' });
  }

  return cadeia;
}

/* O NO QUE HOSPEDA a cadeia — o elo de papel `hospeda`. Existe para que a tela
   possa DIZER de quem e a cadeia, em vez de mostra-la solta sob um produto e
   deixar o leitor concluir que e a rota daquele produto. */
export function hospedeiroDaCadeia(cadeia: ElosCadeia[]): FomNode | null {
  return cadeia.find((e) => e.papel === 'hospeda')?.no ?? null;
}

export type GrupoModalidade = {
  /* o no de modalidade declarado pelos produtos; null = nenhum declarado */
  modal: FomNode | null;
  /* o TIPO verbatim da relacao que sustenta o agrupamento */
  relacao: string | null;
  produtos: FomNode[];
  /* a modalidade e declarada por mais de um produto? (fato do dado) */
  compartilhado: boolean;
};

/* AGRUPAMENTO DE PRODUTOS POR MODALIDADE — derivado, nunca arbitrado.
   A unica aresta que um produto tem na BASE e `utiliza modalidade` (kind
   `trans`). Agrupar por ela e agrupar pelo que o dado declara; qualquer outro
   criterio (nome, prefixo, ordem) seria invencao.
   Produto sem a aresta cai num grupo de modal nulo e a tela DIZ isso — a
   ausencia nunca vira suposicao. Nao ha ordem entre grupos alem da ordem de
   aparicao dos produtos, porque ordem entre modalidades nao existe no dado. */
export function agruparProdutosPorModalidade(
  nos: FomNode[],
  relacoes: Relation[],
): GrupoModalidade[] {
  const porId = new Map(nos.map((n) => [n.external_id, n]));
  const produtos = (PRODUTOS as readonly string[])
    .map((id) => porId.get(id))
    .filter((n): n is FomNode => !!n && !n.removed_at);

  const grupos: GrupoModalidade[] = [];
  const indice = new Map<string, GrupoModalidade>();
  for (const p of produtos) {
    const r = relacoes.find((x) => x.from === p.external_id && /modalidade/i.test(x.type));
    const modal = r ? porId.get(r.to) ?? null : null;
    const chave = modal ? modal.external_id : '__sem-modalidade__';
    let g = indice.get(chave);
    if (!g) {
      g = { modal, relacao: r?.type ?? null, produtos: [], compartilhado: false };
      indice.set(chave, g);
      grupos.push(g);
    }
    g.produtos.push(p);
  }
  for (const g of grupos) g.compartilhado = !!g.modal && g.produtos.length > 1;
  return grupos;
}
